// LiveQueue.js — Monitor de Filas em tempo real (Socket.IO / RF-09)
// - Admin: escolhe o PBX e vê TODAS as filas (pode filtrar por fila).
// - Usuário: vê suas filas permitidas (pode filtrar por fila).
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import API_URL from './config';
import './LiveQueue.css';

function LiveQueue({ token, userRole }) {
  const isAdmin = userRole === 'admin';

  const [queues, setQueues] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState('');

  // Admin: lista de PBX + servidor selecionado.
  const [servers, setServers] = useState([]);
  const [selectedServerId, setSelectedServerId] = useState('');

  // Filtro de fila (todos os perfis).
  const [selectedQueue, setSelectedQueue] = useState('all');

  const socketRef = useRef(null);
  const selectedServerIdRef = useRef('');
  selectedServerIdRef.current = selectedServerId;

  // 1. Admin: carrega os servidores ativos para o seletor.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/users/servers/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setServers(resp.data);
          setSelectedServerId(resp.data[0].id.toString());
        } else {
          setError('Nenhum servidor ativo cadastrado.');
          setStatus('error');
        }
      } catch (e) {
        setError(`Erro ao carregar servidores: ${e.response?.data?.message || e.message}`);
        setStatus('error');
      }
    })();
  }, [isAdmin, token]);

  // 2. Socket.IO: conecta uma única vez (usuário é auto-assinado no backend).
  useEffect(() => {
    const socket = io({ query: { token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('connected');
      // Admin: se já tiver um servidor escolhido, assina imediatamente.
      if (isAdmin && selectedServerIdRef.current) {
        socket.emit('subscribe', { serverId: selectedServerIdRef.current });
      }
    });
    socket.on('queue_update', (data) => {
      setQueues(Array.isArray(data) ? data : []);
    });
    socket.on('ami_error', (msg) => {
      setError(msg);
      setStatus('error');
    });
    socket.on('auth_error', (msg) => {
      setError(msg);
      setStatus('error');
      socket.disconnect();
    });
    socket.on('disconnect', () => setStatus('disconnected'));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, isAdmin]);

  // 3. Admin: ao trocar de servidor, assina o novo servidor e limpa o estado.
  useEffect(() => {
    if (!isAdmin || !selectedServerId) return;
    setSelectedQueue('all');
    setQueues([]);
    setError('');
    const s = socketRef.current;
    if (s && s.connected) {
      s.emit('subscribe', { serverId: selectedServerId });
    }
  }, [isAdmin, selectedServerId]);

  const handleServerChange = (e) => {
    setSelectedServerId(e.target.value);
  };

  const handleQueueChange = (e) => {
    setSelectedQueue(e.target.value);
  };

  const visibleQueues = selectedQueue === 'all'
    ? queues
    : queues.filter((q) => q.name === selectedQueue);

  const statusLabel = {
    'Livre': 'Livre',
    'Ocupado': 'Ocupado',
    'Chamando': 'Chamando',
    'Em Espera': 'Em Espera',
    'Pausado': 'Pausado',
    'Indisponível': 'Indisponível',
    'Desconhecido': 'Desconhecido'
  };

  const getStatusClass = (s) => {
    if (s === 'Livre') return 'lq-free';
    if (s === 'Ocupado' || s === 'Chamando') return 'lq-busy';
    if (s === 'Pausado') return 'lq-paused';
    return 'lq-unknown';
  };

  return (
    <div className="liveQueueContainer">
      <h1 className="pageTitle">Monitor de Filas (tempo real)</h1>

      <div className="lq-status">
        {status === 'connecting' && <span className="lq-connecting">Conectando...</span>}
        {status === 'connected' && <span className="lq-connected">● Conectado</span>}
        {status === 'disconnected' && <span className="lq-disconnected">○ Desconectado</span>}
        {status === 'error' && <span className="lq-disconnected">Erro</span>}
      </div>

      {error && <div className="message messageError">{error}</div>}

      {/* Seletores */}
      <div className="lq-controls">
        {isAdmin && servers.length > 0 && (
          <div className="lq-control">
            <label htmlFor="lq-server-select">PBX:</label>
            <select
              id="lq-server-select"
              value={selectedServerId}
              onChange={handleServerChange}
              className="formSelect"
            >
              {servers.map((server) => (
                <option key={server.id} value={server.id.toString()}>
                  {server.name || `PBX ${server.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {queues.length > 0 && (
          <div className="lq-control">
            <label htmlFor="lq-queue-select">Fila:</label>
            <select
              id="lq-queue-select"
              value={selectedQueue}
              onChange={handleQueueChange}
              className="formSelect"
            >
              <option value="all">Todas as filas ({queues.length})</option>
              {queues.map((queue) => (
                <option key={queue.name} value={queue.name}>
                  {queue.label ? `${queue.label} (${queue.name})` : queue.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Estados */}
      {isAdmin && !selectedServerId && status !== 'error' && (
        <div className="noDataMessage">Selecione um PBX para monitorar as filas.</div>
      )}

      {status === 'connected' && queues.length === 0 && !isAdmin && (
        <div className="noDataMessage">Nenhuma fila monitorada no momento.</div>
      )}

      {status === 'connected' && queues.length === 0 && isAdmin && selectedServerId && (
        <div className="noDataMessage">Nenhuma fila encontrada neste PBX.</div>
      )}

      {/* Fila selecionada sem dados após o filtro */}
      {status === 'connected' && queues.length > 0 && visibleQueues.length === 0 && (
        <div className="noDataMessage">A fila selecionada não possui dados no momento.</div>
      )}

      {visibleQueues.length > 0 && (
        <div className="tableContainer">
          {visibleQueues.map((queue) => (
            <div key={queue.name} className="lq-queue-card">
              <h3 className="lq-queue-name">
                {queue.label ? `${queue.label} (fila ${queue.name})` : `Fila ${queue.name}`}
              </h3>
              {queue.members && queue.members.length > 0 ? (
                <table className="extensionsTable">
                  <thead>
                    <tr>
                      <th>Ramal</th>
                      <th>Status</th>
                      <th>Duração</th>
                      <th>Conectado a</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.members.map((member, idx) => (
                      <tr key={`${member.extension}-${idx}`}>
                        <td>
                          <div className="lq-member-ext">{member.extension}</div>
                          {member.name && (
                            <div className="lq-member-name" title={member.name}>{member.name}</div>
                          )}
                        </td>
                        <td>
                          <span className={`statusBadge ${getStatusClass(member.status)}`}>
                            {statusLabel[member.status] || member.status}
                          </span>
                        </td>
                        <td>{member.callDuration ? `${member.callDuration}s` : '-'}</td>
                        <td>{member.connectedTo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="lq-empty">Sem membros nesta fila.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LiveQueue;
